import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CleaningScopeResponse } from '@/shared/api';
import { useApi } from '@/shared/api';
import type { CleaningBoardView } from './board/boardModel';
import { assigneeOptions } from './day/cleaningDayModel';
import { buildOperationalApartmentRows } from './calendar/cleaningOperationalRows';
import { startOfWeekIso, weekDays } from './calendar/cleaningCalendarHelpers';

export function useCleaningBoardQueries(
  group: CleaningScopeResponse | null,
  date: string,
  view: CleaningBoardView,
  assignee: string,
  selectedId: string | null,
) {
  const api = useApi();
  const weekDates = useMemo(() => weekDays(startOfWeekIso(date)), [date]);
  const propertyIds = group?.apartments.map((apartment) => apartment.id) ?? [];
  const baseDay = useQuery({
    queryKey: ['cleaning', 'day', group?.id, date],
    queryFn: () => api.cleaning.list({ scheduledFor: date, propertyIds }),
    enabled: Boolean(group),
  });
  const filteredDay = useQuery({
    queryKey: ['cleaning', 'day', group?.id, date, assignee],
    queryFn: () =>
      api.cleaning.list({ scheduledFor: date, propertyIds, assignedToUserId: assignee }),
    enabled: Boolean(group && assignee),
  });
  const baseWeek = useQuery({
    queryKey: ['cleaning', 'week', group?.id, weekDates[0], weekDates[6]],
    queryFn: () => api.cleaning.summary({ from: weekDates[0]!, to: weekDates[6]!, propertyIds }),
    enabled: Boolean(group && view === 'calendar'),
  });
  const filteredWeek = useQuery({
    queryKey: ['cleaning', 'week', group?.id, weekDates[0], weekDates[6], assignee],
    queryFn: () =>
      api.cleaning.summary({
        from: weekDates[0]!,
        to: weekDates[6]!,
        propertyIds,
        assignedToUserId: assignee,
      }),
    enabled: Boolean(group && view === 'calendar' && assignee),
  });
  const detail = useQuery({
    queryKey: ['cleaning', 'detail', selectedId],
    queryFn: () => api.cleaning.getDetail(selectedId!),
    enabled: Boolean(selectedId),
  });
  const dayQuery = assignee ? filteredDay : baseDay;
  const weekQuery = assignee ? filteredWeek : baseWeek;
  const dayTasks = dayQuery.data ?? [];
  const weekTasks = weekQuery.data ?? [];
  const people = assigneeOptions([
    ...(baseDay.data ?? []),
    ...(baseWeek.data ?? []).map((task) => ({
      assignedToUserId: task.assignedToUserId,
      assignedToUserName: task.assignedToUserName,
    })),
  ]);
  // Hat die gewählte Person an diesem Tag nichts, fiele sie aus der Auswahl, und das Feld zeigte
  // „Alle“, obwohl die Liste gefiltert ist.
  if (assignee && !people.some((person) => person.id === assignee)) {
    people.push({ id: assignee, name: 'Gewählte Person' });
  }
  const names = new Map(
    group?.apartments.map(
      (apartment) => [apartment.id, apartment.internalName ?? apartment.name] as const,
    ),
  );
  return {
    weekDates,
    dayTasks,
    /** Die gerade gezeigte Abfrage (Tag oder Woche, mit oder ohne Personenfilter) für ihre Zustände. */
    dayQuery,
    /** Der ganze Tag ohne Personenfilter – für den Satz zur Lage des Tages. */
    allDayQuery: baseDay,
    weekQuery,
    rows: buildOperationalApartmentRows(weekTasks, weekDates, (id) => names.get(id)),
    people,
    detail,
  };
}
